import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Card,
  Divider,
  Stack,
  Tooltip,
  Button,
  LinearProgress,
  Fab,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { ChatResponse } from "../../models/chat.model";
import { useDisclosure } from "@mantine/hooks";
import {
  chat,
  clearChatHistory,
  getChatHistory,
  getSpeechFromText,
} from "../../services/chat.service";
import { AddRounded, VolumeUp, VolumeOff } from "@mui/icons-material";

interface ChatUIProps {
  agentName: string;
  agentId: string;
  onCloseClick?: () => void;
}

const ICEBREAKER_MESSAGE = `
  Hey, can you
  1. Introduce yourself in simple words and in one line no more than that. 
  2. Do not use any technical terms.
  3. Ask the me what they would like assistance with.
  4. Keep the tone friendly and approachable.
  `;
const isIcebreakerMessage = (msg: ChatResponse) => {
  return msg.text === ICEBREAKER_MESSAGE;
};

export default function ChatUI({
  agentName,
  onCloseClick,
  agentId,
}: ChatUIProps) {
  const [isLoading, { open: setLoading, close: stopLoading }] =
    useDisclosure(false);
  const [
    isHistoryLoading,
    { open: setHistoryLoading, close: stopHistoryLoading },
  ] = useDisclosure(false);
  const [messages, setMessages] = useState<ChatResponse[]>([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [isAgentAudioAllowed, { toggle: toggleAgentAudio }] =
    useDisclosure(true);

  // Store audio element refs in a map: messageIndex -> HTMLAudioElement
  const audioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  const playAudioForMessage = useCallback(
    async (text: string, index: number) => {
      try {
        const blob = await getSpeechFromText(text);
        const url = URL.createObjectURL(blob);

        // If there's already an audio for this message, clean it up
        const existingAudio = audioRefs.current.get(index);
        if (existingAudio) {
          existingAudio.pause();
          existingAudio.remove();
          URL.revokeObjectURL(existingAudio.src);
        }

        const audio = document.createElement("audio");
        audio.src = url;
        audio.autoplay = true;
        audio.style.display = "none";
        document.body.appendChild(audio);

        // Save ref
        audioRefs.current.set(index, audio);

        audio.onended = () => {
          audio.remove();
          URL.revokeObjectURL(url);
          audioRefs.current.delete(index);
        };
      } catch (error) {
        console.error("Error generating / playing audio:", error);
      }
    },
    []
  );

  const handleSend = async (customMessage?: string) => {
    if (!customMessage && !input.trim()) return;
    setLoading();
    const newMsg: ChatResponse = { text: customMessage ?? input, role: "user" };
    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    try {
      const agentResponse = await chat(agentId, customMessage ?? input);
      setMessages((prev) => [...prev, agentResponse]);

      if (isAgentAudioAllowed) {
        const msgIndex = messages.length + 1; // approximate index of new message
        playAudioForMessage(agentResponse.text, msgIndex);
      }
    } catch (error) {
      console.error("Chat error:", error);
      alert("Failed to send message.");
    } finally {
      stopLoading();
    }
  };

  const sendIcebreakerMessage = async () => {
    await handleSend(ICEBREAKER_MESSAGE);
  };

  const fetchAndLoadChatHistory = useCallback(async () => {
    try {
      setHistoryLoading();
      let history = await getChatHistory(agentId);
      if (history.length === 0) {
        await sendIcebreakerMessage();
        history = await getChatHistory(agentId);
      }
      setMessages(history);
    } catch (error) {
      console.error("History fetch error:", error);
      alert("Failed to load chat history.");
    } finally {
      stopHistoryLoading();
    }
  }, [agentId]);

  useEffect(() => {
    fetchAndLoadChatHistory();
  }, [agentId, fetchAndLoadChatHistory]);

  useEffect(() => {
    (messagesEndRef.current as any)?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSend();
  };

  const onNewChatClick = async () => {
    setLoading();
    setHistoryLoading();
    try {
      await clearChatHistory(agentId);
      await sendIcebreakerMessage();
      await fetchAndLoadChatHistory();
    } catch (error) {
      console.error("Error starting new chat:", error);
    } finally {
      stopLoading();
      stopHistoryLoading();
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        maxWidth: 500,
        height: 600,
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative", // important for absolute positioning inside
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: "#075E54", color: "white", p: 2, width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 18 }}>
            Chat with {agentName || "Agent"}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Tooltip title="New Chat">
              <IconButton
                onClick={onNewChatClick}
                size="small"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  p: 1,
                  borderRadius: 2,
                }}
              >
                <AddRounded />
              </IconButton>
            </Tooltip>
            {onCloseClick && (
              <IconButton
                onClick={onCloseClick}
                size="small"
                sx={{ color: "white", p: 1 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Chat list */}
      <Box
        sx={{ flex: 1, overflowY: "auto", p: 2, backgroundColor: "#ECE5DD" }}
      >
        {isHistoryLoading ? (
          <Stack sx={{ height: "100%", width: "100%" }} justifyContent="center">
            <Typography sx={{ fontSize: 12 }}>Loading chats...</Typography>
            <LinearProgress color="success" />
          </Stack>
        ) : (
          <List sx={{ position: "relative" }}>
            {messages.map((msg, index) => (
              <ListItem
                key={index}
                sx={{
                  display: "flex",
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                  mb: 1,
                  position: "relative", // make it a positioned ancestor
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    p: 1.2,
                    px: 1.8,
                    borderRadius:
                      msg.role === "user"
                        ? "16px 16px 0 16px"
                        : "16px 16px 16px 0",
                    bgcolor: msg.role === "user" ? "#DCF8C6" : "white",
                    maxWidth: "75%",
                    boxShadow: "0 1px 1px rgba(0,0,0,0.15)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "#111" }}
                  >
                    {msg.text}
                    {msg.role !== "user" && (
                      <Stack>
                        <IconButton
                          size="small"
                          sx={{ width: "fit-content" }}
                          onClick={() => playAudioForMessage(msg.text, index)}
                        >
                          <VolumeUp
                            sx={{
                              fontSize: "inherit",
                              p: 0,
                              m: 0,
                              pt: 0.3,
                              display: "inline",
                            }}
                          />
                        </IconButton>
                      </Stack>
                    )}
                  </Typography>

                  {/* Floating Play Button for this message */}
                </Card>
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      <Divider />

      {/* Input */}
      <Box
        sx={{ display: "flex", alignItems: "center", p: 1, bgcolor: "#f0f0f0" }}
      >
        <Tooltip title={`Agent Voice ${isAgentAudioAllowed ? "On" : "Off"}`}>
          <IconButton
            sx={{ color: "#075E54", mr: 1 }}
            onClick={toggleAgentAudio}
          >
            {isAgentAudioAllowed ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
        </Tooltip>

        <TextField
          variant="outlined"
          placeholder="Type a message"
          size="small"
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{
            bgcolor: "white",
            borderRadius: "20px",
            "& .MuiOutlinedInput-root": { borderRadius: "20px" },
          }}
        />

        <IconButton
          onClick={() => handleSend()}
          sx={{ color: "#075E54", ml: 1 }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}
